import { Navigate, useNavigate } from 'react-router-dom';
import { useFlags, useKaliningradMe } from '../api/hooks';
import { ErrorView, Loader } from '../components/StateViews';
import { kgdNum } from '../components/qualification/Kaliningrad';
import { track } from '../analytics';
import { shortDate } from '../format';
import type { KgdSlot } from '../api/types';

const slotDateFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const slotDate = (iso: string) => slotDateFmt.format(new Date(iso)).replace('.', '');

function BackChevron() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

/** Слот сетки 2×4: очки + место + дата; худший зачётный — «вылетит первым». */
function FullSlot({ s }: { s: KgdSlot }) {
  const zero = s.points === 0;
  return (
    <div className={`kqm-slot${zero ? ' kqm-slot--zero' : ''}${s.isWorstCounted ? ' kqm-slot--worst' : ''}`}>
      {s.isWorstCounted && <span className="kqm-worst mono">Вылетит первым</span>}
      <span className={`pts mono${zero ? ' muted' : ''}`}>{kgdNum(s.points)}</span>
      <span className="meta">
        {s.place !== null ? <b>{s.place}-е из {s.field}</b> : <b>без места</b>}
        {' · '}
        <i>{slotDate(s.playedAt)}</i>
      </span>
    </div>
  );
}

/** Экран 2 — «Моя позиция», полный (8a). */
export function KaliningradMeScreen() {
  const navigate = useNavigate();
  const flags = useFlags();
  const q = useKaliningradMe(Boolean(flags.data?.kaliningrad));
  if (flags.data && !flags.data.kaliningrad) return <Navigate to="/qualification" replace />;
  if (!flags.data || q.isLoading) return <Loader />;
  if (q.isError) return <ErrorView message={(q.error as Error)?.message} />;
  const data = q.data;
  if (!data || data.status === 'none' || !data.me) return <Navigate to="/qualification" replace />;
  const me = data.me;
  const finished = data.status === 'finished';
  const slots = data.slots ?? [];
  const games = data.games ?? [];
  const free = me.bestCount - slots.length;
  const nextId = data.nextTournament?.id ?? null;

  return (
    <div className="kqm-screen">
      <div className="kqm-nav">
        <button className="kqm-back" onClick={() => navigate(-1)} aria-label="Назад">
          <BackChevron />
        </button>
        <span className="ttl">Моя позиция</span>
      </div>

      {!me.hasPlayed ? (
        <div className="kq-empty">
          <p>Ещё не играл — любая игра добавит очки.</p>
        </div>
      ) : (
        <>
          <div className="kq-nums kqm-nums">
            <div>
              <div className="kq-label mono">Лучшие {me.bestCount} из 16 игр</div>
              <div className="kq-big kqm-big mono">{kgdNum(me.total ?? 0)}</div>
            </div>
            <div className="kq-nums-right">
              <div className="kq-label mono">Место</div>
              <div className="kq-big kqm-big mono">{me.position}</div>
            </div>
          </div>
          {!finished && (
            <div className="kq-dist">
              {me.inZone
                ? me.leadOver5th !== null && me.leadOver5th !== undefined
                  ? `Отрыв от 5-го — ${kgdNum(me.leadOver5th)} очков`
                  : 'Едешь'
                : me.gapToTop4 !== null && me.gapToTop4 !== undefined
                  ? `До 4-го места — ${kgdNum(me.gapToTop4)} очков`
                  : ''}
            </div>
          )}

          <div className="kqm-sect">
            <span className="ttl">Зачёт</span>
            <span className="sub mono">
              {me.slotsFilled} из {me.bestCount} · сыграно {me.gamesPlayed}
            </span>
          </div>
          <div className="kqm-grid">
            {slots.map((s, i) => (
              <FullSlot key={i} s={s} />
            ))}
            {Array.from({ length: Math.max(0, free) }, (_, i) => (
              <div key={`e${i}`} className="kqm-slot kqm-slot--free">свободно</div>
            ))}
          </div>

          {!finished && (
            <div className="kq-card kqm-card">
              {free > 0 ? (
                <>Любая игра добавит очки — <b>свободно {free === 1 ? '1 слот' : `${free} ${free < 5 ? 'слота' : 'слотов'}`}</b>. После них новая игра заменит худший результат.</>
              ) : me.neededPoints !== null && me.neededPoints !== undefined ? (
                <>Чтобы обойти 4-е место, нужен результат <b>от {kgdNum(me.neededPoints)} очков</b>{me.neededPlace ? ` — это ${me.neededPlace.place}-е место при ${me.neededPlace.field} игроках` : ''}</>
              ) : (
                <>Зачёт полный — каждая следующая игра заменяет худший результат.</>
              )}
            </div>
          )}

          <div className="kqm-sect">
            <span className="ttl">Все игры отбора</span>
            <span className="sub">новые сверху</span>
          </div>
          <div className="kqm-games">
            {games.map((g) => (
              <div key={g.gameId} className={`kqm-game${g.counted ? '' : ' kqm-game--out'}`}>
                <span className="dt mono">{shortDate(g.playedAt)}</span>
                <span className="mid">
                  <b>{g.place !== null ? `${g.place}-е из ${g.field}` : `из ${g.field}`}</b>
                  <i>
                    пул {kgdNum(g.pool)}
                    {!g.counted && ' · не в зачёте'}
                    {g.corrected && ' · исправлено'}
                  </i>
                </span>
                <span className={`pts mono${g.points === 0 ? ' muted' : ''}`}>{kgdNum(g.points)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {nextId !== null && !finished && (
        <div className="kqm-ctawrap">
          <button
            className="kq-cta"
            onClick={() => {
              track('kgd_register_tap', { from: 'me' });
              navigate(`/tournaments/${nextId}`);
            }}
          >
            Записаться на игру
          </button>
        </div>
      )}
    </div>
  );
}
